import React from 'react';
import QRCode from 'react-qr-code';
import { fetchQuery, graphql, useMutation } from 'react-relay';
import environment from 'src/relay/environment';
import config from 'src/shared/config';
import styled from 'styled-components';
import CompletedFineTuningIcon from '../../../shared/icons/completed-finetuning-icon.svg';

interface DeepARProps {
    url: string;
    onSettingNewDeepARSettings: any;
    settings?: any;
}

const QRCodeContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
    h2 {
        margin-bottom: 20px;
    }
    p {
        margin-top: 20px;
    }
`;

const QrCodeLabelandAContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-top: 20px;

    a {
        color: #f46200;
        cursor: pointer;
    }
`;

const UpdateOrCreateDeepARTaskMutation = graphql`
    mutation deepARUpdateOrCreateMutation($input: DeepArTaskInput!) {
        updateOrCreateDeepArTask(input: $input) {
            deepArTask {
                id
                url
                state
                settings {
                    position {
                        x
                        y
                        z
                    }
                    rotation {
                        x
                        y
                        z
                    }
                    scale
                    environment
                }
            }
        }
    }
`;

const DeepARTaskQuery = graphql`
    query deepARTaskPollingQuery($id: ID!) {
        deepArTask(id: $id) {
            state
            settings {
                position {
                    x
                    y
                    z
                }
                rotation {
                    x
                    y
                    z
                }
                scale
                environment
            }
        }
    }
`;

const DeepAR: React.FC<DeepARProps> = ({ url, settings, onSettingNewDeepARSettings }) => {
    // variabili deepAR mutation
    // const [id, setId] = React.useState('guid://Zakeke/DeepArTask/-1');
    console.log('DeepAR component mounted with:', { url, settings });
    const [id, setId] = React.useState(null);
    const [state, setState] = React.useState('WAITING');

    const intermediateLightEnvironment =
        'https://zakekecdn.blob.core.windows.net/cdn/3d/assets/environments/deepAR_HDRs/Warehouse-with-lights_(intermediate)_new.hdr';

    const [position, setPosition] = React.useState(settings && settings.position ? settings.position : { x: 0, y: 0, z: 0 });
    const [rotation, setRotation] = React.useState(settings && settings.rotation ? settings.rotation : { x: 0, y: 0, z: 0 });
    const [scale, setScale] = React.useState(settings && settings.scale ? settings.scale : 1.0);
    const [environmentMap, setEnvironmentMap] = React.useState(settings && settings.environment ? settings.environment : intermediateLightEnvironment);
    const [taskID, setTaskID] = React.useState();
    const [stopPolling, setStopPolling] = React.useState(false);
    const [token, setToken] = React.useState(config.token);
    const [isCompletedFineTuning, setIsCompletedFineTuning] = React.useState(false);
    const [showCompletedSection, setShowCompletedSection] = React.useState(false);
    const [initialSettings, setInitialSettings] = React.useState(false);

    const [deepARUrl, setDeepARUrl] = React.useState(() => {
        console.log('Initial deepARUrl state - url:', url, 'taskID:', taskID);
        const params = new URLSearchParams({
            fileUrl: url,
            taskID: taskID || '',
            positionX: position.x.toString(),
            positionY: position.y.toString(),
            positionZ: position.z.toString(),
            rotationX: rotation.x.toString(),
            rotationY: rotation.y.toString(),
            rotationZ: rotation.z.toString(),
            scale: scale.toString(),
            enviroment: environmentMap,
            token: token!.toString(),
            isFineTuning: 'true'
        });

        return `${config.deepAR.split(';')[0]}?${params.toString()}`;
    });

    const [updateOrCreateDeepARTaskMutation, updateOrCreateDeepARTaskMutationInProgress] = useMutation(UpdateOrCreateDeepARTaskMutation);

    const handleCreateDeepARTaskMutation = () => {
        const input = {
            id,
            url,
            state,
            settings: {
                position,
                rotation,
                scale,
                environment: environmentMap
            }
        };

        updateOrCreateDeepARTaskMutation({
            variables: { input },
            onCompleted: (response: any) => {
                const newTaskID = response.updateOrCreateDeepArTask.deepArTask.id;
                console.log('Task Created with ID:', newTaskID, 'Full response:', response);
                setTaskID(newTaskID);
            },
            onError: (error) => {
                console.error('Error creating task:', error);
            }
        });
    };

    const fetchTaskState = (taskID: number) => {
        fetchQuery(environment, DeepARTaskQuery, { id: taskID })
            .toPromise()
            .then((response: any) => {
                console.log('Task state:', response?.deepArTask?.state);
                if (response && response.deepArTask?.state === 'COMPLETED') {
                    console.log('Task completata, settings ricevuti :' + JSON.stringify(response.deepArTask.settings, null, 2));
                    setPosition(response.deepArTask.settings.position);
                    setRotation(response.deepArTask.settings.rotation);
                    setScale(response.deepArTask.settings.scale);
                    setEnvironmentMap(response.deepArTask.settings.environment);
                    onSettingNewDeepARSettings(response.deepArTask.settings);
                    setIsCompletedFineTuning(true);

                    setStopPolling(true);
                }
            })
            .catch((error) => {
                console.error('Error fetching task state:', error);
            });
    };

    // Uso document.referrer per controllare la pagina precedente

    React.useEffect(() => {
        const previousUrl = localStorage.getItem('previousUrl');
        console.log(previousUrl);
        if (previousUrl && previousUrl.includes('buttons?product_id=' + window.location.search.split('=')[1])) {
            console.log('hai visitato la pagina dei tasti, quindi mostro la sezione completata');
            setShowCompletedSection(true);
        }
    }, []);

    // Imposto i settings iniziali, che l'utente vada o meno avanti con il fine-tuning
    React.useEffect(() => {
        console.log('imposto i setting iniziali al primo caricamento pagina', settings);
        if (settings && !initialSettings) {
            onSettingNewDeepARSettings(settings);
            setInitialSettings(true);
        }
    }, []);

    // creo la task deepAR
    React.useEffect(() => {
        if (!showCompletedSection) handleCreateDeepARTaskMutation();
    }, []);

    // Polling del task deepAR
    React.useEffect(() => {
        if (!taskID || stopPolling) return;
        if (!isCompletedFineTuning && !showCompletedSection) {
            const intervalId = setInterval(() => {
                console.log('sto controllando lo stato della task con ID: ', taskID);
                fetchTaskState(taskID);
            }, 5000); // Polling ogni 5 sec

            return () => clearInterval(intervalId); // Pulizia del polling quando `stopPolling` o `taskID` cambiano
        }
    }, [taskID, stopPolling, isCompletedFineTuning]);

    // Quando vuoi aggiornare deepARUrl, chiama setDeepARUrl:
    React.useEffect(() => {
        console.log('useEffect triggered - url:', url, 'taskID:', taskID);
        const params = new URLSearchParams({
            fileUrl: url,
            taskID: taskID || '',
            positionX: position.x.toString(),
            positionY: position.y.toString(),
            positionZ: position.z.toString(),
            rotationX: rotation.x.toString(),
            rotationY: rotation.y.toString(),
            rotationZ: rotation.z.toString(),
            scale: scale.toString(),
            environment: environmentMap,
            token: token!.toString(),
            isFineTuning: 'true'
        });
        const newUrl = `${config.deepAR.split(';')[0]}?${params.toString()}`;
        console.log('Setting new deepARUrl:', newUrl);
        setDeepARUrl(newUrl);
    }, [url, taskID, position, rotation, scale, environmentMap, isCompletedFineTuning]);

    console.log({ deepARUrl });

    return !showCompletedSection && deepARUrl !== '' && taskID !== '' && !isCompletedFineTuning ? (
        <QRCodeContainer>
            <QRCode value={deepARUrl} size={256} />
            <h2>Scan QR code</h2>
            <p>
                To visualize the VTO on your phone and use the fine-tuning tools, please scan the QR code. Once you're done, return to your desktop to complete
                the process.
            </p>
        </QRCodeContainer>
    ) : (
        <QRCodeContainer>
            <CompletedFineTuningIcon />
            <h2>Fine-tuning completed</h2>
            <span>Here's a revised version to better reflect the context: Fine-tuning is correctly configured. You can now proceed to the next step.</span>
            <QrCodeLabelandAContainer>
                <span> If you need to revisit the QR code to make adjustments, </span>
                <a
                    onClick={() => {
                        handleCreateDeepARTaskMutation();
                        setShowCompletedSection(false);
                        setIsCompletedFineTuning(false);
                        setStopPolling(false);
                    }}
                >
                    click here.
                </a>
            </QrCodeLabelandAContainer>
        </QRCodeContainer>
    );
};

export default DeepAR;
